import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';

import { googleToInternalPlaceDetails } from '../../utils/googlePlaces';
import useGooglePlaces from './googlePlaces/useGooglePlaces';

export interface AddressValue {
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  zipcode: string;
  county?: string;
}

export type AddressFieldInput = Partial<
  Record<keyof AddressValue, string | undefined>
> | null;

export interface AddressFieldProps {
  value: AddressFieldInput;
  onChange: (address: AddressValue) => void;
  disabled?: boolean;
  required?: boolean;
  showCounty?: boolean;
  showAddressLine2?: boolean;
  showFields?: boolean;
  size?: 'small' | 'medium';
  errors?: Partial<Record<keyof AddressValue, string>>;
  searchLabel?: string;
  stateOptions?: { value: string; label: string }[];
}

const normalizeAddress = (input: AddressFieldInput): AddressValue => ({
  ...{
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zipcode: '',
    county: '',
  },
  ...Object.fromEntries(
    Object.entries(input ?? {}).map(([k, v]) => [k, v ?? ''])
  ),
});

const formatAddressDisplay = (addr: AddressFieldInput): string => {
  if (!addr?.address_line_1?.trim()) return '';
  return [
    addr.address_line_1,
    addr.address_line_2,
    addr.city,
    [addr.state, addr.zipcode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
};

type PlaceOption = { label: string; id: string };

const GoogleAddressField: React.FC<AddressFieldProps> = ({
  value: rawValue,
  onChange,
  disabled = false,
  required = false,
  showCounty = false,
  showAddressLine2 = true,
  showFields = true,
  size = 'medium',
  errors,
  searchLabel = 'Search for an address',
  stateOptions,
}) => {
  const value = normalizeAddress(rawValue);
  const { placePredictions, getPlacePredictions } = useGooglePlaces({
    apiKey: 'AIzaSyAc1YTQ2fOO28hxTxwOHFkaYb5cByCmIbU',
  });

  const [searchInput, setSearchInput] = useState(() =>
    formatAddressDisplay(rawValue)
  );
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const options: PlaceOption[] = placePredictions
    .filter(Boolean)
    .map((pred) => ({
      label: pred?.text?.text || '',
      id: pred?.placeId || '',
    }));

  const handlePlaceSelect = useCallback(
    (_: React.SyntheticEvent, selected: string | PlaceOption | null) => {
      if (!selected || typeof selected === 'string') return;

      const prediction = placePredictions.find(
        (pred) => pred?.placeId === selected.id
      );
      if (!prediction) return;

      setSearchInput(selected.label);
      setIsLoadingDetails(true);

      const place = prediction.toPlace();
      place
        .fetchFields({ fields: ['displayName', 'addressComponents'] })
        .then(() => {
          const details = googleToInternalPlaceDetails(place);
          onChange({
            address_line_1: details.address,
            address_line_2: '',
            city: details.city,
            state: details.state,
            zipcode: details.zip,
            county: details.county,
          });
        })
        .catch(() => {
          /* user can still type manually */
        })
        .finally(() => setIsLoadingDetails(false));
    },
    [placePredictions, onChange]
  );

  const handleFieldChange = useCallback(
    (field: keyof AddressValue) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...value, [field]: e.target.value });
    },
    [value, onChange]
  );

  return (
    <Box>
      <Autocomplete
        freeSolo
        blurOnSelect
        disableClearable={disabled}
        disabled={disabled}
        open={options.length > 0 && isFocused && searchInput.length > 2}
        options={options}
        filterOptions={(x) => x}
        inputValue={searchInput}
        onInputChange={(_, val, reason) => {
          setSearchInput(val);
          if (reason === 'input' && val.length > 2) {
            getPlacePredictions({ input: val });
          }
        }}
        onChange={handlePlaceSelect}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderOption={({ key, ...props }, option) => (
          <li {...props} key={key}>
            <LocationOnIcon
              fontSize="small"
              sx={{ mr: 1, color: 'text.secondary' }}
            />
            <Typography noWrap>{option.label}</Typography>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={searchLabel}
            size={size}
            placeholder="Start typing an address..."
          />
        )}
      />

      {showFields && (
        <Paper
          elevation={0}
          sx={{ p: 2.5, mt: 2, bgcolor: 'grey.50', borderRadius: 1 }}
        >
          {isLoadingDetails && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                Loading address details...
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            size={size}
            label="Address line 1"
            value={value.address_line_1}
            onChange={handleFieldChange('address_line_1')}
            required={required}
            disabled={disabled}
            error={!!errors?.address_line_1}
            helperText={errors?.address_line_1}
            autoComplete="off"
            sx={{ mb: 2.5 }}
          />
          {showAddressLine2 && (
            <TextField
              fullWidth
              size={size}
              label="Address line 2"
              value={value.address_line_2}
              onChange={handleFieldChange('address_line_2')}
              disabled={disabled}
              error={!!errors?.address_line_2}
              helperText={errors?.address_line_2}
              autoComplete="off"
              sx={{ mb: 2.5 }}
            />
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
            <TextField
              fullWidth
              size={size}
              label="City"
              value={value.city}
              onChange={handleFieldChange('city')}
              required={required}
              disabled={disabled}
              error={!!errors?.city}
              helperText={errors?.city}
              autoComplete="off"
            />

            {stateOptions ? (
              <Autocomplete
                fullWidth
                options={stateOptions}
                getOptionLabel={(opt) =>
                  typeof opt === 'string' ? opt : `${opt.label} (${opt.value})`
                }
                value={
                  stateOptions.find((o) => o.value === value.state) ?? null
                }
                onChange={(_, selected) =>
                  onChange({
                    ...value,
                    state: selected?.value ?? '',
                  })
                }
                disabled={disabled}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    size={size}
                    required={required}
                    error={!!errors?.state}
                    helperText={errors?.state}
                  />
                )}
              />
            ) : (
              <TextField
                fullWidth
                size={size}
                label="State"
                value={value.state}
                onChange={handleFieldChange('state')}
                required={required}
                disabled={disabled}
                error={!!errors?.state}
                helperText={errors?.state}
                autoComplete="off"
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size={size}
              label="Zip"
              value={value.zipcode}
              onChange={handleFieldChange('zipcode')}
              required={required}
              disabled={disabled}
              error={!!errors?.zipcode}
              helperText={errors?.zipcode}
              autoComplete="off"
            />

            {showCounty && (
              <TextField
                fullWidth
                size={size}
                label="County"
                value={value.county || ''}
                onChange={handleFieldChange('county')}
                disabled={disabled}
                error={!!errors?.county}
                helperText={errors?.county}
                autoComplete="off"
              />
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default GoogleAddressField;
